import { formatDate } from "date-fns";

import { KeywordSearch } from "@/types/search";
import { BidFavoriteFilter, GetBidListRequest, ValueType } from "@/types/bid";
import { TabMenu } from "@/types/Shared/tab";
import { SearchConditionItem } from "@/types/setting";

import { subtractDays } from "@/libs/utils";

export enum ProjectType {
  Original = "original",
  Procure = "procure",
  BidPlan = "bid-plan",
}

export const DomesticTabData: TabMenu[] = [
  { label: "입찰 공고", path: "/bid/domestic/list" },
  { label: "사전 규격", path: "/bid/domestic/specification" },
  { label: "발주 계획", path: "/bid/domestic/plan" },
];
export const DefaultBidSearchKeywordList: KeywordSearch[] = [
  { title_or_doc: "TITLE", or_and: "OR", input_strings: [] },
  { title_or_doc: "DOC", or_and: "OR", input_strings: [] },
  { title_or_doc: "TITLE", or_and: "OR", input_strings: [] },
  { title_or_doc: "TITLE", or_and: "OR", input_strings: [] },
  { title_or_doc: "TITLE", or_and: "OR", input_strings: [] },
];

export const DefaultBidSearchDateRange = "30";

export const BidTypeCd: Record<string, string> = {
  ALL: "전체",
  TYPE_01: "용역",
  TYPE_02: "물품",
  TYPE_03: "공사",
  TYPE_04: "외자",
  TYPE_05: "기타",
};

export const categoryList: ValueType[] = [
  { value: "TYPE_01", label: "용역" },
  { value: "TYPE_02", label: "물품" },
  { value: "TYPE_03", label: "공사" },
  { value: "TYPE_04", label: "외자" },
  { value: "TYPE_05", label: "기타" },
];
export const enterpriseList: ValueType[] = [
  { label: "문서 참조 필요", value: "CHECK" },
  { label: "기업제한 있음", value: "NOT_ALLOWED" },
  { label: "기업제한 없음", value: "ALLOWED" },
];
export const sortList: ValueType[] = [
  { value: "relevance|desc", label: "정확도" },
  { value: "uploaded_dt|desc", label: "게시일 내림차순" },
  { value: "uploaded_dt|asc", label: "게시일 오름차순" },
  { value: "close_dt|desc", label: "마감일 내림차순" },
  { value: "close_dt|asc", label: "마감일 오름차순" },
  { value: "own_product_ratio|desc", label: "자사 제품 내림차순" },
  { value: "own_product_ratio|asc", label: "자사 제품 오름차순" },
];

export const pageSizeList: ValueType[] = [
  { value: "10", label: "10개" },
  { value: "20", label: "20개" },
  { value: "50", label: "50개" },
  { value: "100", label: "100개" },
];
export const internationList: ValueType[] = [
  { value: "Y", label: "국제입찰" },
  { value: "N", label: "국내입찰" },
];

export const cancelledFilterList: ValueType[] = [
  { value: "ALL", label: "전체" },
  { value: "EXCLUDE", label: "취소공고 제외" },
  { value: "ONLY", label: "취소공고만" },
];

export const DateRangeList: ValueType[] = [
  { label: "하루 전", value: "1" },
  { label: "일주일 전", value: "7" },
  { label: "한 달 전", value: "30" },
  { label: "일 년 전", value: "365" },
  { label: "전체 조회", value: "999" },
  { label: "자유 입력", value: "0" },
];

export const DefaultBidSearch: GetBidListRequest = {
  keyword_sets: DefaultBidSearchKeywordList,
  notice_no: [],
  exclude_strings: [],
  title_exclude_strings: [],
  project_type: "",
  page: 1,
  page_size: 10,
  budget_no_limit: true,
  min_budget: 0,
  max_budget: 0,
  include_finished: false,
  date_max_range: false,
  min_date: formatDate(
    subtractDays(new Date(), Number(DefaultBidSearchDateRange)).toString(),
    "yyyy-MM-dd"
  ),
  max_date: formatDate(new Date().toString(), "yyyy-MM-dd"),
  date_range_option_index: DefaultBidSearchDateRange,
  category: "ALL",
  enterprise_restriction: "ALL",
  sort_by: "uploaded_dt",
  order: "desc",
  international_flag: "ALL",
  license_check: false,
  product_check: false,
  consortium_check: false,
  performance_flag: "NO_RESTRICTION",
  expert_flag: "NO_RESTRICTION",
  own_product_ratio: 0,
  region_filter: [],
  dminst_filter: [],
  license_filter: [],
  products_filter: [],
  cancelled_filter: "EXCLUDE", // 기본값: 취소공고 제외
};

export const DefaultFavoriteFilter: BidFavoriteFilter = {
  project_type: "",
  title: "",
  dminst_filter: "",
  category: "ALL",
  sort_by: "budget_amount",
  order: "desc",
};

export const BidFavoriteTabData: TabMenu[] = [
  {
    label: "입찰 공고",
    path: "/bid/favorite/list",
  },
  {
    label: "사전 규격",
    path: "/bid/favorite/specification",
  },
  {
    label: "발주 계획",
    path: "/bid/favorite/plan",
  },
];

export const getFilterToString = (
  filter: SearchConditionItem,
  type: string = "search"
) => {
  const { settings } = filter;
  const searchKeywordList = settings.keyword_sets
    .filter(
      (keyword, index) =>
        JSON.stringify(keyword) !==
        JSON.stringify(DefaultBidSearchKeywordList[index])
    )
    .map(
      ({ title_or_doc, or_and, input_strings }) =>
        `BidNoticeKeywordSet(title_or_doc="${title_or_doc}", or_and="${or_and}", input_strings=[${input_strings.length > 0 ? `"${input_strings.join('", "')}"` : ""}])`
    )
    .join(", ");

  return (
    `search_request = BidNoticeSearchRequest(` +
    `keyword_sets=[${searchKeywordList}], ` +
    `notice_no=${JSON.stringify(settings.notice_no)}, ` +
    `exclude_strings=${JSON.stringify(settings.exclude_strings)}, ` +
    `title_exclude_strings=${JSON.stringify(settings.title_exclude_strings)}, ` +
    `min_date="${formatDate(new Date(settings.min_date).toString(), "yyyy-MM-dd")}", ` +
    `max_date="${formatDate(new Date(settings.max_date).toString(), "yyyy-MM-dd")}", ` +
    `date_max_range=${settings.date_max_range ? "True" : "False"}, ` +
    `date_range_option_index=${settings.date_range_option_index}, ` +
    `include_finished=${settings.include_finished ? "True" : "False"}, ` +
    `min_budget=${settings.min_budget}, ` +
    `max_budget=${settings.max_budget}, ` +
    `budget_no_limit=${settings.budget_no_limit ? "True" : "False"}, ` +
    `category="${settings.category}", ` +
    `enterprise_restriction="${settings.enterprise_restriction}", ` +
    `sort_by="${settings.sort_by}", ` +
    `order="${settings.order}", ` +
    `international_flag="${settings.international_flag}", ` +
    `license_check=${settings.license_check ? "True" : "False"}, ` +
    `product_check=${settings.product_check ? "True" : "False"}, ` +
    `consortium_check=${settings.consortium_check ? "True" : "False"}, ` +
    `performance_flag="${settings.performance_flag}", ` +
    `expert_flag="${settings.expert_flag}", ` +
    `own_product_ratio=${settings.own_product_ratio}, ` +
    `${type === "search" ? `project_type="${settings.project_type}", page=${settings.page}, page_size=${settings.page_size},` : ""}` +
    `dminst_filter=${JSON.stringify(settings.dminst_filter.map((dminst) => dminst.value))}, ` +
    `products_filter=${JSON.stringify(settings.products_filter.map((product) => product.value))}, ` +
    `license_filter=${JSON.stringify(settings.license_filter.map((license) => license.value))}, ` +
    `region_filter=${JSON.stringify(settings.region_filter.map((region) => region.value))}, ` +
    `cancelled_filter="${settings.cancelled_filter ?? "EXCLUDE"}")\n`
  );
};

export const getSearchFilterToString = (filter: SearchConditionItem) =>
  `${getFilterToString(filter)}` +
  `starred_projects = bid.notice.star.get_projects("${filter.settings.project_type}")\n` +
  `project_ids = {project.project_id for project in starred_projects.projects}\n` +
  `search_result=bid.notice.search_by_keywords(search_request)\n` +
  `for item in search_result.items:\n` +
  `    if item.project_id in project_ids:\n` +
  `        item.starred = True\n` +
  `    else:\n` +
  `        item.starred = False\n` +
  `return search_result`;

export const getSearchConditionFilterToString = (
  selectedSearchCondition: SearchConditionItem
) =>
  `${getFilterToString(selectedSearchCondition, "searchCondition")}` +
  `request=[UserSearchSettingsRequest(` +
  `search_id="${selectedSearchCondition.search_id}",` +
  `settings=search_request, ` +
  `search_name="${selectedSearchCondition.search_name}", ` +
  `pinned="${selectedSearchCondition.pinned}", ` +
  `user_ids=${JSON.stringify(selectedSearchCondition.users.map((user) => user.user_id))})]`;

// 입찰공고 업종 검색 필터
export const BidNoticeSuggestSearchLicenseList =
  "bid.notice.suggest.search_licenses";
// 입찰공고 물품번호 검색 필터
export const BidNoticeSuggestSearchProductList =
  "bid.notice.suggest.search_products";
// 입찰공고 기관 검색 필터
export const BidNoticeSuggestSearchClientList =
  "bid.notice.suggest.search_clients";
